import gql from 'graphql-tag'
import moment from 'moment-timezone'

export const LOGIN_MUTATION = gql`
  mutation LoginMutation($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
      }
    }
  }
`

export const SIGNUP_MUTATION = gql`
  mutation SignupMutation($firstName: String!, $lastName: String!, $email: String!, $password: String!) {
    signup(firstName: $firstName, lastName: $lastName, email: $email, password: $password) {
      token
      user {
        id
      }
    }
  }
`

export const METRICS_QUERY = gql`
  query MetricsQuery($where: MetricWhereInput!) {
    metrics(where: $where) {
      data
    }
  }
`

export const RELATED_METRICS_QUERY = gql`
  query RelatedMetricsQuery($where: MetricWhereInput!) {
    relatedMetrics: metrics(where: $where) {
      data
    }
  }
`

export const USER_QUERY = gql`
  query UserQuery {
    me {
      id
      firstName
      lastName
      phoneNumber
      email
      role
    }
  }
`

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation changePassword($password: String!, $newPassword: String!) {
    changePassword(password: $password, newPassword: $newPassword) {
      token
    }
  }
`

export const ALERTS_QUERY = gql`
  query AlertsQuery {
    alerts(where: { acknowledged_not: true }) {
      id
      task
      level
      time
      data
      acknowledged
      device {
        name
        site {
          name
        }
      }
    }
  }
`

export const ALERT_SUMMARY_SUBSCRIPTION = gql`
  subscription alertSummaryAdded {
    newAlertSummary {
      node {
        id
        rule {
          label
          reminderInterval
        }
        firstSeen
        lastSeen
        count
        expiresAt
        message
        nextReminder
        acknowledged
        device {
          name
          site {
            name
          }
        }
      }
    }
  }
`

let now = moment().format('YYYY-MM-DDTHH:mm:ssZ')
export const ALERT_SUMMARY_QUERY = gql`
  query AlertSummaryQuery {
    alertSummaries: myAlertSummaries(where: { acknowledged_not: true, expiresAt_gt: "${now}" }) {
      id
      rule {
        label
        reminderInterval
      }
      firstSeen
      lastSeen
      count
      expiresAt
      message
      nextReminder
      acknowledged
      device {
        name
        site {
          name
        }
      }
    }
  }
`

export const ALERT_SUMMARY_ALL_QUERY = gql`
  query AlertSummaryAllQuery {
    alertSummaries: myAlertSummaries(where: { expiresAt_gt: "${now}" }) {
      id
      rule {
        label
        reminderInterval
      }
      firstSeen
      lastSeen
      count
      expiresAt
      message
      nextReminder
      acknowledged
      device {
        name
        site {
          name
        }
      }
    }
  }
`

export const TARGETS_QUERY = gql`
  query TargetsQuery {
    targets: myTargets {
      id
      enabled
      label
      method
      destination
    }
  }
`

export const RULES_QUERY = gql`
  query RulesQuery {
    rules: rules {
      id
      uuid
      enabled
      label
      expiresIn
      reminderInterval
      aggPeriod
      aggFunction
      metricName
      triggerValue
      comparator
      devices {
        id
        name
        type
        serial
      }
      targets {
        id
        label
        destination
      }
      actions {
        id
        type
        params
        enabled
      }
    }
  }
`

export const COMPANIES_QUERY = gql`
  query CompaniesQuery {
    companies {
      id
      key
      name
    }
  }
`

export const SITES_QUERY = gql`
  query SitesQuery {
    sites {
      id
      name
    }
  }
`

export const SITES_MANAGEMENT_QUERY = gql`
  query SitesManagementQuery {
    sites {
      id
      key
      name
      type
      city
      state
      company {
        id
        key
        name
      }
    }
  }
`

export const CREATE_SITE_MUTATION = gql`
  mutation createSite($input: SiteCreateInput!) {
    createSite(input: $input) {
      id
      key
      name
      type
      city
      state
      company {
        id
        key
        name
      }
    }
  }
`

export const UPDATE_SITE_MUTATION = gql`
  mutation updateSite($id: String!, $input: SiteUpdateInput!) {
    updateSite(id: $id, input: $input) {
      id
      key
      name
      type
      city
      state
      company {
        id
        key
        name
      }
    }
  }
`

export const DELETE_SITE_MUTATION = gql`
  mutation deleteSite($id: String!) {
    deleteSite(id: $id) {
      id
    }
  }
`

export const DEVICES_QUERY = gql`
  query DevicesQuery {
    devices(where: { enabled: true }) {
      id
      name
      type
      subtype
      serial
      version
      leafSerial
      enabled
      startDate
      endDate
      site {
        id
        name
      }
      latitude
      longitude
      elevation
      relatedTo {
        id
        name
        serial
        type
        elevation
      }
      relatedBy {
        id
        name
        serial
        type
        elevation
      }
    }
  }
`

export const DEVICES_HEALTH_QUERY = gql`
  query DevicesHealthQuery {
    devices {
      id
      name
      serial
      type
      subtype
      enabled
      site {
        name
      }
      config {
        publishInt
        loopInt
        keepAlive
        voltLimit
        stayAwake
      }
    }
  }
`

export const ALL_DEVICES_QUERY = gql`
  query AllDevicesQuery {
    devices {
      id
      name
      type
      subtype
      serial
      version
      leafSerial
      enabled
      startDate
      endDate
      site {
        id
        name
      }
      latitude
      longitude
      elevation
      relatedTo {
        id
        name
        serial
        type
        elevation
      }
      relatedBy {
        id
        name
        serial
        type
        elevation
      }
    }
  }
`

export const CREATE_RULE_MUTATION = gql`
  mutation createRule($data: RuleCreateInput!) {
    createRule(input: $data) {
      id
    }
  }
`

export const UPDATE_RULE_MUTATION = gql`
  mutation updateRule($data: RuleUpdateInput!, $id: String!) {
    updateRule(input: $data, id: $id) {
      id
    }
  }
`

export const DELETE_RULE_MUTATION = gql`
  mutation deleteRule($id: String!) {
    deleteRule(id: $id) {
      id
    }
  }
`

export const CREATE_TARGET_MUTATION = gql`
mutation createTarget($data: TargetCreateInput!) {
  createTarget(input: $data) {
    id
  }
}
`

export const UPDATE_TARGET_MUTATION = gql`
  mutation updateTarget($data: TargetUpdateInput!, $id: String!) {
    updateTarget(input: $data, id: $id) {
      id
    }
  }
`

export const DELETE_TARGET_MUTATION = gql`
  mutation deleteTarget($id: String!) {
    deleteTarget(id: $id) {
      id
    }
  }
`

export const UPDATE_ALERT_SUMMARY_MUTATION = gql`
  mutation updateAlertSummary($data: AlertSummaryUpdateInput!, $id: String!) {
    updateAlertSummary(input: $data, id: $id) {
      id
    }
  }
`

export const UPDATE_USER_MUTATION = gql`
  mutation updateUser($data: UserUpdateInput!, $id: String!) {
    updateUser(input: $data, id: $id) {
      id
    }
  }
`

export const TEST_ALERT_DELIVERY_MUTATION = gql`
  mutation testAlertDelivery($targetId: String!) {
    testAlertDelivery(targetId: $targetId) {
      success
      message
    }
  }
`

export const UPDATE_USER_ROLE_MUTATION = gql`
  mutation updateUserRole($id: String!, $role: Role!) {
    updateUserRole(id: $id, role: $role) {
      id
      role
    }
  }
`

export const ADMIN_RESET_USER_PASSWORD_MUTATION = gql`
  mutation adminResetUserPassword($id: String!, $newPassword: String!) {
    adminResetUserPassword(id: $id, newPassword: $newPassword) {
      id
    }
  }
`

export const ADD_USER_TO_TEAM_MUTATION = gql`
  mutation addUserToTeam($teamId: String!, $userId: String!, $role: Role!) {
    addUserToTeam(teamId: $teamId, userId: $userId, role: $role) {
      id
      role
      team { id name }
      user { id firstName lastName email }
    }
  }
`

export const REMOVE_USER_FROM_TEAM_MUTATION = gql`
  mutation removeUserFromTeam($id: String!) {
    removeUserFromTeam(id: $id) {
      id
    }
  }
`

export const ADD_DEVICE_TO_TEAM_MUTATION = gql`
  mutation addDeviceToTeam($teamId: String!, $deviceId: String!, $permission: Permission!) {
    addDeviceToTeam(teamId: $teamId, deviceId: $deviceId, permission: $permission) {
      id
      permission
      team { id name }
      device { id name serial type }
    }
  }
`

export const UPDATE_TEAM_DEVICE_MUTATION = gql`
  mutation updateTeamDevice($id: String!, $permission: Permission!) {
    updateTeamDevice(id: $id, permission: $permission) {
      id
      permission
    }
  }
`

export const REMOVE_DEVICE_FROM_TEAM_MUTATION = gql`
  mutation removeDeviceFromTeam($id: String!) {
    removeDeviceFromTeam(id: $id) {
      id
    }
  }
`

export const DEVICE_CONFIG_QUERY = gql`
  query DeviceConfigQuery($serial: String!) {
    deviceConfigBySerial(serial: $serial) {
      id
      type
      subtype
      company
      loopInt
      publishInt
      keepAlive
      stayAwake
      sleepType
      bmeCal
      minChgTmp
      voltLimit
      currentLimit
      chargeVolt
      adcMin
      adcMax
      sensors {
        id
        label
        type
        unit
        cal
        channel
        iface
        max
        min
        nr
      }
      device {
        id
        name
        serial
      }
    }
  }
`

export const UPDATE_DEVICE_CONFIG_MUTATION = gql`
  mutation updateDeviceConfig($id: String!, $input: DeviceConfigUpdateInput!) {
    updateDeviceConfig(id: $id, input: $input) {
      id
      type
      subtype
      company
      loopInt
      publishInt
      keepAlive
      stayAwake
      sleepType
      bmeCal
      minChgTmp
      voltLimit
      currentLimit
      chargeVolt
      adcMin
      adcMax
      sensors {
        id
        label
        type
        unit
        cal
        channel
        iface
        max
        min
        nr
      }
    }
  }
`

export const CREATE_DEVICE_CONFIG_MUTATION = gql`
  mutation createDeviceConfig($input: DeviceConfigCreateInput!) {
    createDeviceConfig(input: $input) {
      id
      type
      subtype
      company
      loopInt
      publishInt
      keepAlive
      stayAwake
      sleepType
      bmeCal
      minChgTmp
      voltLimit
      currentLimit
      chargeVolt
      adcMin
      adcMax
      sensors {
        id
        label
        type
        unit
        cal
        channel
        iface
        max
        min
        nr
      }
      device {
        id
        name
        serial
      }
    }
  }
`

export const DELETE_DEVICE_SENSOR_MUTATION = gql`
  mutation deleteDeviceSensor($id: String!) {
    deleteDeviceSensor(id: $id) {
      id
    }
  }
`

export const CREATE_DEVICE_SENSOR_MUTATION = gql`
  mutation createDeviceSensor($configId: String!, $input: DeviceSensorCreateInput!) {
    createDeviceSensor(configId: $configId, input: $input) {
      id
      label
      type
      unit
      cal
      channel
      iface
      max
      min
      nr
    }
  }
`

export const DEVICE_SENSOR_QUERY = gql`
  query DeviceSensorQuery($id: String!) {
    deviceSensor(id: $id) {
      id
      label
      type
      unit
      cal
      channel
      iface
      max
      min
      nr
      config {
        id
        adcMin
        adcMax
        type
        subtype
        device {
          id
          name
          serial
          type
          elevation
          site {
            name
          }
        }
      }
    }
  }
`

export const UPDATE_DEVICE_SENSOR_MUTATION = gql`
  mutation updateDeviceSensor($id: String!, $input: DeviceSensorUpdateInput!) {
    updateDeviceSensor(id: $id, input: $input) {
      id
      label
      type
      unit
      cal
      channel
      iface
      max
      min
      nr
    }
  }
`

// Admin User Management Queries and Mutations
export const ALL_USERS_QUERY = gql`
  query AllUsersQuery {
    users {
      id
      firstName
      lastName
      email
      phoneNumber
      role
    }
  }
`

// Team Management Queries
export const TEAMS_QUERY = gql`
  query TeamsQuery {
    teams {
      id
      key
      name
      company {
        id
        name
      }
    }
  }
`

export const TEAM_MEMBERS_QUERY = gql`
  query TeamMembersQuery {
    teamMembers {
      id
      role
      team {
        id
        key
        name
      }
      user {
        id
        firstName
        lastName
        email
      }
    }
  }
`

export const TEAM_DEVICES_QUERY = gql`
  query TeamDevicesQuery {
    teamDevices {
      id
      permission
      team {
        id
        key
        name
      }
      device {
        id
        name
        serial
        type
      }
    }
  }
`

// Device Management Mutations
export const UPDATE_DEVICE_MUTATION = gql`
  mutation updateDevice($id: String!, $input: DeviceUpdateInput!) {
    updateDevice(id: $id, input: $input) {
      id
      enabled
      elevation
      site {
        id
        name
      }
    }
  }
`

export const DELETE_METRIC_EVENTS_MUTATION = gql`
  mutation deleteMetricEvents($input: DeleteMetricEventsInput!) {
    deleteMetricEvents(input: $input) {
      count
      deviceId
      deviceType
    }
  }
`

export const UPDATE_METRIC_SERIALS_MUTATION = gql`
  mutation updateMetricSerials($input: UpdateMetricSerialsInput!) {
    updateMetricSerials(input: $input) {
      count
      deviceId
      deviceType
      oldSerial
      newSerial
    }
  }
`

export const AUDIT_LOGS_QUERY = gql`
  query AuditLogsQuery($skip: Int, $limit: Int, $orderBy: AuditLogOrderByInput, $where: AuditLogWhereInput) {
    auditLogs(skip: $skip, limit: $limit, orderBy: $orderBy, where: $where) {
      id
      action
      entityType
      entityId
      entityName
      userId
      userEmail
      changes
      createdAt
    }
  }
`

export const CREATE_DEVICE_MUTATION = gql`
  mutation createDevice($input: DeviceCreateInput!) {
    createDevice(input: $input) {
      id
      name
      type
      subtype
      serial
      version
      leafSerial
      enabled
      startDate
      endDate
      site {
        id
        name
      }
      latitude
      longitude
      elevation
    }
  }
`

export const MANUAL_READINGS_QUERY = gql`
  query ManualReadingsQuery($where: ManualReadingWhereInput, $skip: Int, $limit: Int, $orderBy: ManualReadingOrderByInput) {
    manualReadings(where: $where, skip: $skip, limit: $limit, orderBy: $orderBy) {
      id
      deviceId
      metricName
      value
      unit
      timestamp
      notes
      isManual
      isFieldVerified
      source
      batchId
      createdAt
      updatedAt
      device {
        id
        name
        serial
        type
      }
      createdBy {
        id
        firstName
        lastName
        email
      }
    }
  }
`

export const MANUAL_READING_BATCHES_QUERY = gql`
  query ManualReadingBatchesQuery($deviceId: String) {
    manualReadingBatches(deviceId: $deviceId) {
      batchId
      deviceId
      filename
      count
      dateRangeStart
      dateRangeEnd
      importedAt
      importedBy {
        id
        firstName
        lastName
        email
      }
      device {
        id
        name
        serial
        type
      }
    }
  }
`

export const CREATE_MANUAL_READING_MUTATION = gql`
  mutation createManualReading($input: ManualReadingCreateInput!) {
    createManualReading(input: $input) {
      id
      deviceId
      metricName
      value
      unit
      timestamp
      notes
      isManual
      isFieldVerified
      source
      batchId
      createdAt
    }
  }
`

export const UPDATE_MANUAL_READING_MUTATION = gql`
  mutation updateManualReading($id: String!, $input: ManualReadingUpdateInput!) {
    updateManualReading(id: $id, input: $input) {
      id
      deviceId
      metricName
      value
      unit
      timestamp
      notes
      isManual
      isFieldVerified
      source
      batchId
      updatedAt
    }
  }
`

export const DELETE_MANUAL_READING_MUTATION = gql`
  mutation deleteManualReading($id: String!) {
    deleteManualReading(id: $id) {
      id
    }
  }
`

export const DELETE_MANUAL_READING_BATCH_MUTATION = gql`
  mutation deleteManualReadingBatch($batchId: String!) {
    deleteManualReadingBatch(batchId: $batchId) {
      batchId
      count
    }
  }
`

export const LINK_DEVICES_MUTATION = gql`
  mutation linkDevices($deviceId: String!, $relatedDeviceId: String!) {
    linkDevices(deviceId: $deviceId, relatedDeviceId: $relatedDeviceId) {
      id
      relatedTo {
        id
        name
        serial
        type
        elevation
      }
      relatedBy {
        id
        name
        serial
        type
        elevation
      }
    }
  }
`

export const UNLINK_DEVICES_MUTATION = gql`
  mutation unlinkDevices($deviceId: String!, $relatedDeviceId: String!) {
    unlinkDevices(deviceId: $deviceId, relatedDeviceId: $relatedDeviceId) {
      id
      relatedTo {
        id
        name
        serial
        type
        elevation
      }
      relatedBy {
        id
        name
        serial
        type
        elevation
      }
    }
  }
`

export const RULE_ACTION_TYPES_QUERY = gql`
  query RuleActionTypesQuery {
    ruleActionTypes
  }
`

export const CREATE_RULE_ACTION_MUTATION = gql`
  mutation createRuleAction($ruleId: String!, $input: RuleActionCreateInput!) {
    createRuleAction(ruleId: $ruleId, input: $input) {
      id
      type
      params
      enabled
    }
  }
`

export const UPDATE_RULE_ACTION_MUTATION = gql`
  mutation updateRuleAction($id: String!, $input: RuleActionUpdateInput!) {
    updateRuleAction(id: $id, input: $input) {
      id
      type
      params
      enabled
    }
  }
`

export const DELETE_RULE_ACTION_MUTATION = gql`
  mutation deleteRuleAction($id: String!) {
    deleteRuleAction(id: $id) {
      id
    }
  }
`
